import React, { Component } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { withTheme, defaultTheme } from '@elixir/theme';
import styles from './InlineNotification-hashed.scss';
import getIconClasses from '../../iconUtils';


const types = {
    WARNING: 'warning',
    ERROR: 'error',
    INFO: 'info',
    SUCCESS: 'success'
};

const defaultText = {
    [types.SUCCESS]: {
        id: 'elixir.notification.success',
        defaultMessage: 'Success:'
    },
    [types.INFO]: {
        id: 'elixir.notification.info',
        defaultMessage: 'Info:'
    },
    [types.WARNING]: {
        id: 'elixir.notification.warning',
        defaultMessage: 'Warning:'
    },
    [types.ERROR]: {
        id: 'elixir.notification.error',
        defaultMessage: 'Error:'
    }
};

export class InlineNotification extends Component {
    static propTypes = {
        /**
        * CSS class name for this component
        **/
        className: PropTypes.string,
        /**
        * Text to display on notification
        **/
        message: PropTypes.string,
        /**
        * Phrase allowing to set color and appearance of notification (success, warning, error, info)
        **/
        type: PropTypes.oneOf([types.SUCCESS, types.INFO, types.WARNING, types.ERROR]),
        /**
        * Theme config that's supposed to be applied to the component
        **/
        theme: PropTypes.object,
        /**
        * If true, the dismiss button appears with this component
        **/
        isDismissable: PropTypes.bool
    };

    static contextTypes = {
        translator: PropTypes.func.isRequired
    };

    static defaultProps = {
        isDismissable: true,
        type: types.INFO,
        theme: defaultTheme
    };

    state = {
        dismissed: true
    };

    componentDidUpdate(prevProps) {
        if (prevProps.type !== this.props.type ||
            prevProps.message !== this.props.message) {
            this.setState({ dismissed: true });
        }
    }

    handleDismiss = () => {
        this.setState({ dismissed: false });
    };

    getDefaultMessage(type) {
        const defaultMessage = defaultText[type];
        const { translator } = this.context;
        return (translator) ? translator(defaultMessage) : defaultMessage;
    }

    renderTypeIcon(type, componentStyles = {}) {
        const iconClasses = cx(
            { [componentStyles.icon]: type === types.SUCCESS },
            { [getIconClasses('far check-circle')]: type === types.SUCCESS },
            { [getIconClasses('info')]: type === types.INFO },
            { [getIconClasses('exclamation')]: type === types.WARNING },
            { [getIconClasses('exclamation')]: type === types.ERROR },
            { 'fa-stack-1x': type !== types.SUCCESS }
        );
        const iconCircleClasses = cx(
            getIconClasses('far circle'),
            'fa-stack-2x',
        );

        const iconDiv = <i className={iconClasses} />;
        const iconCircleDiv = <i className={iconCircleClasses} />;

        const wrapperClasses = cx('fa-stack', componentStyles.iconWrapper);

        const spanWrapper = (
            <span className={wrapperClasses}>
                {iconDiv}
                {iconCircleDiv}
            </span>
        );

        return (type !== types.SUCCESS) ? spanWrapper : iconDiv;
    }

    render() {

        if (!this.state.dismissed) {
            return null;
        }

        const {
            className, message, type, theme, isDismissable
        } = this.props;

        const { translator } = this.context;

        const themeStyles = theme.getStyles('InlineNotification', styles);

        const classes = cx(
            themeStyles.inlineNotification,
            {
                [themeStyles.inlineNotificationSuccess]: type === types.SUCCESS,
                [themeStyles.inlineNotificationInfo]: type === types.INFO,
                [themeStyles.inlineNotificationWarning]: type === types.WARNING,
                [themeStyles.inlineNotificationError]: type === types.ERROR
            },
            className
        );

        const dismissIconClasses = cx(themeStyles.dismissButton, getIconClasses('times'));
        const dismissButton = (<i className={dismissIconClasses} onKeyPress={this.handleDismiss}
            onClick={this.handleDismiss} role='button' tabIndex='0' />);

        const typeIcon = this.renderTypeIcon(type, themeStyles);

        const notification = (<div className={classes}>
            {typeIcon}
            <span className={themeStyles.message}><span className={themeStyles.messageDefault}>{this.getDefaultMessage(type)}</span>{translator(message)}</span>
            {isDismissable ? dismissButton : null}
        </div>);

        return notification;
    }
}

export default withTheme(InlineNotification);
